import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal as TerminalIcon, Plus, X, ScrollText, Monitor, GripVertical, GripHorizontal, Database, Table, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import VoiceInputButton from '../components/VoiceInputButton';
import type { SimpleTerminalHandle } from '../components/SimpleTerminal';

// Spaceship styling
import '../styles/spaceship.css';

// Control Center Components
import StatusHeader from '../components/control-center/StatusHeader';
import { ShellUIContext } from '../components/tools/ShellUIContext';
import DirectoryTree from '../components/control-center/DirectoryTree';
import BottomDrawer from '../components/control-center/BottomDrawer';
import OrchestratorPanel from '../components/control-center/OrchestratorPanel';
import TaskQueue from '../components/control-center/TaskQueue';
import ProcessManager from '../components/control-center/ProcessManager';
import ZomboidServices from '../components/control-center/ZomboidServices';
import { ToastNotification, useToasts } from '../components/control-center/ToastNotification';
import { useHealthMonitor } from '../hooks/useHealthMonitor';
import SimpleTerminal from '../components/SimpleTerminal';
import LogHistoryTerminal from '../components/LogHistoryTerminal';

// Config
const LOG_SERVER = '/api/claude-logs';
const CLAUDE_LOGS_STORAGE_KEY = 'claude-logs-terminals-v1';

// Terminal slot types
interface TerminalSlot {
  id: string;
  name: string;
}

// Default slots (used on first load)
const DEFAULT_TERMINAL_SLOTS: TerminalSlot[] = [
  { id: 'terminal-1', name: 'Shell 1' },
  { id: 'terminal-2', name: 'Shell 2' },
  { id: 'terminal-3', name: 'Shell 3' },
  { id: 'terminal-4', name: 'Shell 4' },
];

// Generate unique ID for new terminals
let claudeLogsTerminalCounter = 5;
const generateTerminalId = (): string => {
  const id = `terminal-${claudeLogsTerminalCounter}`;
  claudeLogsTerminalCounter++;
  return id;
};

// Load slots from storage - RESET: always use defaults for now
const loadTerminalSlots = (): TerminalSlot[] => {
  // Clear any old localStorage data
  try { localStorage.removeItem(CLAUDE_LOGS_STORAGE_KEY); } catch {}
  return [...DEFAULT_TERMINAL_SLOTS];
};

// Load active terminal from storage - RESET: always terminal-1
const loadActiveTerminal = (): string => {
  return 'terminal-1';
};

// Save state to storage
const saveTerminalState = (slots: TerminalSlot[], activeId: string) => {
  try {
    localStorage.setItem(CLAUDE_LOGS_STORAGE_KEY, JSON.stringify({ slots, activeId }));
  } catch {}
};

interface LogEntry {
  type: string;
  line: string;
  parsed?: any;
}

type LogType = 'stream' | 'corrections' | 'deviations' | 'overseer';

interface TodoItem {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  activeForm: string;
}

const ClaudeLogsPage: React.FC = () => {
  // Dynamic terminal slots
  const [terminalSlots, setTerminalSlots] = useState<TerminalSlot[]>(loadTerminalSlots);
  const [activeTerminalId, setActiveTerminalId] = useState<string>(loadActiveTerminal);
  const [terminalStatuses, setTerminalStatuses] = useState<Record<string, 'disconnected' | 'connecting' | 'connected'>>({});

  // Resizable panel widths (percentages for horizontal, pixels for vertical)
  const [leftPanelWidth, setLeftPanelWidth] = useState(256); // px
  const [rightPanelWidth, setRightPanelWidth] = useState(384); // px
  const [bottomDrawerHeight, setBottomDrawerHeight] = useState(200); // px

  // Resize refs and state
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef<'left' | 'right' | 'bottom' | null>(null);
  const startPosRef = useRef({ x: 0, y: 0, size: 0 });

  // Terminal refs for voice input injection
  const terminalRefs = useRef<Map<string, SimpleTerminalHandle>>(new Map());

  // Handle resize start
  const handleResizeStart = useCallback((edge: 'left' | 'right' | 'bottom', e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = edge;
    startPosRef.current = {
      x: e.clientX,
      y: e.clientY,
      size: edge === 'left' ? leftPanelWidth : edge === 'right' ? rightPanelWidth : bottomDrawerHeight
    };
    document.body.style.cursor = edge === 'bottom' ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftPanelWidth, rightPanelWidth, bottomDrawerHeight]);

  // Handle resize move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;

      const edge = isResizingRef.current;
      const delta = edge === 'bottom'
        ? startPosRef.current.y - e.clientY
        : edge === 'left'
        ? e.clientX - startPosRef.current.x
        : startPosRef.current.x - e.clientX;

      const newSize = Math.max(150, Math.min(600, startPosRef.current.size + delta));

      if (edge === 'left') setLeftPanelWidth(newSize);
      else if (edge === 'right') setRightPanelWidth(newSize);
      else setBottomDrawerHeight(Math.max(100, Math.min(400, startPosRef.current.size + delta)));
    };

    const handleMouseUp = () => {
      isResizingRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Terminal mode: 'shell' for interactive terminals, 'logs' for log history view, 'database' for DB viewer
  const [terminalMode, setTerminalMode] = useState<'shell' | 'logs' | 'database'>('logs');
  const [logHistoryStatus, setLogHistoryStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');

  // Database viewer state
  const [dbTables, setDbTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableSchema, setTableSchema] = useState<{ table: string; columns: { name: string; type: string; pk: boolean }[]; rowCount: number } | null>(null);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [dbOffset, setDbOffset] = useState(0);
  const [dbLimit] = useState(50);
  const [customQuery, setCustomQuery] = useState('');
  const [queryResult, setQueryResult] = useState<Record<string, unknown>[] | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [dbLoading, setDbLoading] = useState(false);

  // Connection states
  const [sseConnected, setSseConnected] = useState(false);
  const [supabaseConnected, setSupabaseConnected] = useState(true);
  const [processManagerOpen, setProcessManagerOpen] = useState(false);
  const [zomboidServicesOpen, setZomboidServicesOpen] = useState(false);

  // Save state when it changes
  useEffect(() => {
    saveTerminalState(terminalSlots, activeTerminalId);
  }, [terminalSlots, activeTerminalId]);

  // Validate active terminal exists
  useEffect(() => {
    if (!terminalSlots.find(s => s.id === activeTerminalId) && terminalSlots.length > 0) {
      setActiveTerminalId(terminalSlots[0].id);
    }
  }, [terminalSlots, activeTerminalId]);

  // Add a new terminal
  const addTerminal = useCallback(() => {
    const newId = generateTerminalId();
    const newSlot: TerminalSlot = {
      id: newId,
      name: `Shell ${newId.split('-')[1]}`
    };
    setTerminalSlots(prev => [...prev, newSlot]);
    setActiveTerminalId(newId);
  }, []);

  // Remove a terminal
  const removeTerminal = useCallback((terminalId: string) => {
    if (terminalSlots.length <= 1) return;

    setTerminalSlots(prev => prev.filter(s => s.id !== terminalId));
    setTerminalStatuses(prev => {
      const next = { ...prev };
      delete next[terminalId];
      return next;
    });

    // If removing active terminal, select nearest neighbor
    if (activeTerminalId === terminalId) {
      const currentIndex = terminalSlots.findIndex(s => s.id === terminalId);
      const newIndex = currentIndex > 0 ? currentIndex - 1 : 1;
      const newSlot = terminalSlots[newIndex] || terminalSlots.find(s => s.id !== terminalId);
      if (newSlot) setActiveTerminalId(newSlot.id);
    }
  }, [activeTerminalId, terminalSlots]);

  // Update status for a terminal
  const updateTerminalStatus = useCallback((terminalId: string, status: 'disconnected' | 'connecting' | 'connected') => {
    setTerminalStatuses(prev => ({ ...prev, [terminalId]: status }));
  }, []);

  // Toast notifications
  const { toasts, addToast, dismissToast } = useToasts();

  // Health monitoring
  const handleHealthEvent = useCallback((event: any) => {
    const typeMap: Record<string, 'error' | 'warning' | 'success' | 'info'> = {
      service_down: 'error',
      service_recovered: 'success',
      threshold_exceeded: 'warning',
      threshold_recovered: 'info'
    };

    addToast({
      type: typeMap[event.type] || 'info',
      title: event.type === 'service_down' ? `${event.service} Down` :
             event.type === 'service_recovered' ? `${event.service} Recovered` :
             event.type === 'threshold_exceeded' ? `${event.metric?.toUpperCase()} High` :
             `${event.metric?.toUpperCase()} Normalized`,
      message: event.message,
      sound: event.type === 'service_down'
    });

    if (event.type === 'service_down' || event.type === 'threshold_exceeded') {
      fetch(`${LOG_SERVER}/api/alerts/discord`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: event.type === 'service_down' ? 'error' : 'warning',
          title: event.type === 'service_down' ? `${event.service} Down` : `${event.metric?.toUpperCase()} Threshold`,
          message: event.message
        })
      }).catch(() => {});
    }
  }, [addToast]);

  const { health } = useHealthMonitor(handleHealthEvent);

  // Log data for bottom drawer
  const [logs, setLogs] = useState<Record<LogType, LogEntry[]>>({
    stream: [],
    corrections: [],
    deviations: [],
    overseer: []
  });

  const [deviationCount, setDeviationCount] = useState(0);
  const [correctionCount, setCorrectionCount] = useState(0);
  const [todos, setTodos] = useState<TodoItem[]>([]);

  // SSE connections ref
  const sseConnectionsRef = useRef<Map<LogType, EventSource>>(new Map());

  // Current terminal status - use log history status when in logs mode
  const wsConnected = terminalMode === 'logs'
    ? logHistoryStatus === 'connected'
    : terminalStatuses[activeTerminalId] === 'connected';

  // Connect to SSE streams
  const connectSSE = useCallback((type: LogType) => {
    if (sseConnectionsRef.current.has(type)) return;

    const es = new EventSource(`${LOG_SERVER}/stream/${type}?lines=100`);
    es.onopen = () => setSseConnected(true);
    es.onerror = () => setSseConnected(false);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        let parsed;
        try { parsed = JSON.parse(data.line); } catch { parsed = null; }

        setLogs(prev => {
          const current = prev[type];
          const key = parsed?.ts || data.line?.slice(0, 100);
          const isDupe = current.slice(-50).some(e =>
            (e.parsed?.ts === key) || (e.line?.slice(0, 100) === key)
          );
          if (isDupe) return prev;

          const newLogs = [...current, { ...data, parsed }].slice(-200);
          return { ...prev, [type]: newLogs };
        });

        if (type === 'deviations') {
          setDeviationCount(prev => prev + 1);
        } else if (type === 'corrections') {
          setCorrectionCount(prev => prev + 1);
        }

        if (type === 'stream' && parsed) {
          const toolName = parsed.tool || parsed.tool_name || '';
          if (toolName === 'TodoWrite' && parsed.params?.todos) {
            setTodos(parsed.params.todos);
          }
        }
      } catch (e) {
        console.error('SSE parse error:', e);
      }
    };

    sseConnectionsRef.current.set(type, es);
  }, []);

  // Reconnect all SSE
  const reconnectAll = useCallback(() => {
    sseConnectionsRef.current.forEach(es => es.close());
    sseConnectionsRef.current.clear();
    ['stream', 'corrections', 'deviations', 'overseer'].forEach(type => {
      connectSSE(type as LogType);
    });
  }, [connectSSE]);

  // Connect SSE on mount
  useEffect(() => {
    ['stream', 'corrections', 'deviations', 'overseer'].forEach(type => {
      connectSSE(type as LogType);
    });

    return () => {
      sseConnectionsRef.current.forEach(es => es.close());
      sseConnectionsRef.current.clear();
    };
  }, [connectSSE]);

  // Status dot colors
  const getStatusDot = (status: 'disconnected' | 'connecting' | 'connected') => {
    switch (status) {
      case 'connected': return 'bg-green-500';
      case 'connecting': return 'bg-yellow-500 animate-pulse';
      default: return 'bg-red-500';
    }
  };

  // Status text labels
  const getStatusText = (status: 'disconnected' | 'connecting' | 'connected') => {
    switch (status) {
      case 'connected': return 'active';
      case 'connecting': return 'connecting';
      default: return 'deactivated';
    }
  };

  // Extract shell number from active terminal id
  const activeShellId = parseInt(activeTerminalId.replace('terminal-', ''), 10) || 1;

  // Database viewer functions
  const loadDbTables = useCallback(async () => {
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/tables`);
      const data = await res.json();
      setDbTables(data.tables || []);
    } catch (e) {
      console.error('Failed to load tables:', e);
    }
  }, []);

  const loadTableSchema = useCallback(async (table: string) => {
    setDbLoading(true);
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/schema/${table}`);
      const data = await res.json();
      setTableSchema(data);
    } catch (e) {
      console.error('Failed to load schema:', e);
    }
    setDbLoading(false);
  }, []);

  const loadTableData = useCallback(async (table: string, offset = 0) => {
    setDbLoading(true);
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/data/${table}?limit=${dbLimit}&offset=${offset}`);
      const data = await res.json();
      setTableData(data.rows || []);
      setDbOffset(offset);
    } catch (e) {
      console.error('Failed to load data:', e);
    }
    setDbLoading(false);
  }, [dbLimit]);

  const selectDbTable = useCallback((table: string) => {
    setSelectedTable(table);
    setQueryResult(null);
    setQueryError(null);
    loadTableSchema(table);
    loadTableData(table, 0);
  }, [loadTableSchema, loadTableData]);

  const runCustomQuery = useCallback(async () => {
    if (!customQuery.trim()) return;
    setDbLoading(true);
    setQueryError(null);
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: customQuery })
      });
      const data = await res.json();
      if (data.error) {
        setQueryError(data.error);
        setQueryResult(null);
      } else {
        setQueryResult(data.rows);
        setQueryError(null);
      }
    } catch (e) {
      setQueryError(String(e));
    }
    setDbLoading(false);
  }, [customQuery]);

  // Load tables when database mode is selected
  useEffect(() => {
    if (terminalMode === 'database' && dbTables.length === 0) {
      loadDbTables();
    }
  }, [terminalMode, dbTables.length, loadDbTables]);

  // Voice input handler - sends transcript to active terminal
  const handleVoiceTranscript = useCallback((text: string) => {
    const terminalRef = terminalRefs.current.get(activeTerminalId);
    if (terminalRef && text.trim()) {
      // Send text followed by Enter key to execute
      terminalRef.sendInput(text.trim() + '\n');
      terminalRef.focus();
    }
  }, [activeTerminalId]);

  return (
    <div className="flex flex-col h-screen md:h-[calc(100vh-56px)] lg:h-[calc(100vh-80px)] bg-transparent overflow-hidden relative">
      {/* Deep space background layer */}
      <div className="deep-space-bg" />

      {/* Ambient glow effects */}
      <div className="ambient-glow top-left" />
      <div className="ambient-glow bottom-right" />

      <ToastNotification toasts={toasts} onDismiss={dismissToast} />

      {/* StatusHeader hidden on mobile */}
      <div className="hidden md:block">
        <StatusHeader
          sseConnected={sseConnected}
          wsConnected={wsConnected}
          supabaseConnected={supabaseConnected}
          onReconnect={reconnectAll}
          onOpenProcessManager={() => setProcessManagerOpen(true)}
          onOpenZomboidServices={() => setZomboidServicesOpen(true)}
          serviceHealth={health.services}
          systemHealth={health.system}
        />
      </div>

      <ProcessManager
        isOpen={processManagerOpen}
        onClose={() => setProcessManagerOpen(false)}
        serverUrl={LOG_SERVER}
      />

      <ZomboidServices
        isOpen={zomboidServicesOpen}
        onClose={() => setZomboidServicesOpen(false)}
        serverUrl={LOG_SERVER}
      />

      {/* Main Content - 3 column layout, terminal-only on mobile */}
      <div ref={containerRef} className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left Panel - hidden on mobile */}
        <div className="hidden md:flex shrink-0 flex-col side-panel-glass" style={{ width: leftPanelWidth }}>
          <div className="h-2/5 overflow-hidden">
            <DirectoryTree serverUrl={LOG_SERVER} />
          </div>

          <div className="h-[30%] border-t border-gray-800 bg-gray-900/50 flex flex-col">
            <div className="px-3 py-2 border-b border-gray-800/50 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-400">Claude Progress</span>
              <span className="text-xs text-cyan-400">
                {todos.filter(t => t.status === 'completed').length}/{todos.length}
              </span>
            </div>
            {todos.length > 0 && (
              <div className="px-3 py-1">
                <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-green-500 transition-all duration-500"
                    style={{ width: `${(todos.filter(t => t.status === 'completed').length / todos.length) * 100}%` }}
                  />
                </div>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
              {todos.map((todo, idx) => (
                <div
                  key={idx}
                  className={`px-2 py-1 rounded text-[11px] ${
                    todo.status === 'completed'
                      ? 'text-green-400/60 line-through'
                      : todo.status === 'in_progress'
                      ? 'text-cyan-300 bg-cyan-500/10 border-l-2 border-cyan-400'
                      : 'text-gray-500'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    {todo.status === 'completed' && <span>✓</span>}
                    {todo.status === 'in_progress' && <span className="animate-pulse">●</span>}
                    {todo.status === 'pending' && <span className="text-gray-600">○</span>}
                    <span className="truncate">
                      {todo.status === 'in_progress' ? todo.activeForm : todo.content}
                    </span>
                  </div>
                </div>
              ))}
              {todos.length === 0 && (
                <div className="text-center text-gray-600 text-[10px] py-2">No active tasks</div>
              )}
            </div>
          </div>

          <div className="h-[25%] border-t border-gray-800">
            <TaskQueue serverUrl={LOG_SERVER} onSendCommand={() => {}} wsConnected={wsConnected} />
          </div>

          {/* Shell UI Context - element picker embedded in left panel */}
          <div className="border-t border-gray-800 p-2">
            <ShellUIContext shellId={activeShellId} embedded />
          </div>
        </div>

        {/* Left resize handle */}
        <div
          className="hidden md:flex w-1.5 shrink-0 cursor-col-resize items-center justify-center bg-transparent hover:bg-cyan-500/20 transition-colors group"
          onMouseDown={(e) => handleResizeStart('left', e)}
        >
          <div className="w-0.5 h-16 bg-gray-700 group-hover:bg-cyan-400 rounded-full transition-colors" />
        </div>

        {/* Center - Dual Terminal + Bottom Drawer */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden bg-transparent neon-glow-container">

          {/* Terminal Area - takes remaining space above drawer */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Tab bar with mode toggle */}
          <div className="flex items-center gap-1 px-3 py-2 terminal-header shrink-0 overflow-x-auto">
            {/* Mode toggle buttons */}
            <div className="mode-toggle flex items-center gap-0.5 mr-3">
              <button
                onClick={() => setTerminalMode('logs')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  terminalMode === 'logs'
                    ? 'active-logs bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_10px_rgba(6,182,212,0.3)]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
                title="View tool call history"
              >
                <ScrollText className="w-3.5 h-3.5" />
                <span>Logs</span>
              </button>
              <button
                onClick={() => setTerminalMode('shell')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  terminalMode === 'shell'
                    ? 'active-shell bg-green-500/20 text-green-300 border border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.3)]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
                title="Interactive shell"
              >
                <Monitor className="w-3.5 h-3.5" />
                <span>Shell</span>
              </button>
              <button
                onClick={() => setTerminalMode('database')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  terminalMode === 'database'
                    ? 'active-db bg-purple-500/20 text-purple-300 border border-purple-500/40 shadow-[0_0_10px_rgba(168,85,247,0.3)]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
                title="Database viewer"
              >
                <Database className="w-3.5 h-3.5" />
                <span>Database</span>
              </button>
            </div>

            {/* Shell tabs - only show when in shell mode */}
            {terminalMode === 'shell' && (
              <>
                {terminalSlots.map(slot => (
                  <div
                    key={slot.id}
                    className={`terminal-tab group flex items-center gap-1.5 px-3 py-1.5 rounded-md cursor-pointer transition-all text-xs shrink-0 ${
                      activeTerminalId === slot.id
                        ? 'active bg-gradient-to-b from-cyan-500/20 to-cyan-600/10 text-cyan-300 border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.2)]'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2" onClick={() => setActiveTerminalId(slot.id)}>
                      <TerminalIcon className="w-3.5 h-3.5" />
                      <span className="font-medium">{slot.name}</span>
                      <div className={`status-dot ${
                        (terminalStatuses[slot.id] || 'disconnected') === 'connected' ? 'connected' :
                        (terminalStatuses[slot.id] || 'disconnected') === 'connecting' ? 'connecting' : 'disconnected'
                      }`} />
                    </div>
                    {terminalSlots.length > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeTerminal(slot.id); }}
                        className="ml-1 p-0.5 rounded hover:bg-red-500/30 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                        title="Close terminal"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addTerminal}
                  className="holo-button flex items-center gap-1 px-2.5 py-1.5 text-gray-400 hover:text-green-400 transition-all shrink-0"
                  title="Add terminal"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>

                {/* Voice input button */}
                <div className="ml-2 border-l border-gray-700/50 pl-2">
                  <VoiceInputButton
                    onTranscript={handleVoiceTranscript}
                    showTranscript={true}
                  />
                </div>
              </>
            )}

            {/* Log history status - show when in logs mode */}
            {terminalMode === 'logs' && (
              <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
                <div className={`status-dot ${
                  logHistoryStatus === 'connected' ? 'connected' :
                  logHistoryStatus === 'connecting' ? 'connecting' : 'disconnected'
                }`} />
                <span className="text-gray-300 font-medium">Tool Call Stream</span>
                <span className={`text-xs ${
                  logHistoryStatus === 'connected' ? 'neon-text-green' :
                  logHistoryStatus === 'connecting' ? 'text-yellow-400' : 'text-red-400'
                }`}>{getStatusText(logHistoryStatus)}</span>
              </div>
            )}
          </div>

          {/* Terminal instances - shell terminals, log history, and database viewer */}
          <div className="flex-1 relative terminal-glass m-2 rounded-lg overflow-hidden">
            {/* Subtle scanlines overlay */}
            <div className="scanlines" />

            {/* Log History Terminal - shown when mode is 'logs' */}
            <div className={`absolute inset-0 ${terminalMode === 'logs' ? '' : 'hidden'}`}>
              <LogHistoryTerminal
                visible={terminalMode === 'logs'}
                onStatusChange={setLogHistoryStatus}
              />
            </div>

            {/* Shell Terminals - shown when mode is 'shell' */}
            {terminalSlots.map(slot => (
              <div key={slot.id} className={`absolute inset-0 ${terminalMode === 'shell' && activeTerminalId === slot.id ? '' : 'hidden'}`}>
                <SimpleTerminal
                  ref={(handle) => {
                    if (handle) {
                      terminalRefs.current.set(slot.id, handle);
                    } else {
                      terminalRefs.current.delete(slot.id);
                    }
                  }}
                  id={slot.id}
                  visible={terminalMode === 'shell' && activeTerminalId === slot.id}
                  onStatusChange={(status) => updateTerminalStatus(slot.id, status)}
                />
              </div>
            ))}

            {/* Database Viewer - shown when mode is 'database' */}
            <div className={`absolute inset-0 flex bg-[#0d1117] ${terminalMode === 'database' ? '' : 'hidden'}`}>
              {/* Table List Sidebar */}
              <div className="w-44 bg-[#161b22] border-r border-gray-800 flex flex-col">
                <div className="px-3 py-2 border-b border-gray-800 font-medium text-xs flex items-center gap-2 text-purple-400">
                  <Database className="w-3.5 h-3.5" /> overseer.db
                </div>
                <div className="flex-1 overflow-y-auto">
                  {dbTables.map((table) => (
                    <button
                      key={table}
                      onClick={() => selectDbTable(table)}
                      className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-800/50 transition-colors ${
                        selectedTable === table ? 'bg-purple-600/20 text-purple-300 border-l-2 border-purple-500' : 'text-gray-400'
                      }`}
                    >
                      <Table className="w-3 h-3" /> {table}
                    </button>
                  ))}
                  {dbTables.length === 0 && (
                    <div className="px-3 py-4 text-xs text-gray-500 text-center">Loading tables...</div>
                  )}
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Custom Query Bar */}
                <div className="p-2 border-b border-gray-800 bg-[#161b22]">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customQuery}
                      onChange={(e) => setCustomQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runCustomQuery()}
                      placeholder="SELECT * FROM sessions LIMIT 10"
                      className="flex-1 bg-[#0d1117] border border-gray-700 rounded px-3 py-1.5 text-xs font-mono text-gray-200 focus:outline-none focus:border-purple-500 placeholder-gray-600"
                    />
                    <button
                      onClick={runCustomQuery}
                      disabled={dbLoading}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 rounded flex items-center gap-1.5 text-xs font-medium transition-colors"
                    >
                      <Play className="w-3 h-3" /> Run
                    </button>
                  </div>
                  {queryError && (
                    <div className="mt-2 text-red-400 text-xs font-mono bg-red-500/10 px-2 py-1 rounded">{queryError}</div>
                  )}
                </div>

                {/* Schema Info */}
                {tableSchema && !queryResult && (
                  <div className="px-3 py-2 border-b border-gray-800 bg-[#161b22]/50">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-medium text-sm text-white">{tableSchema.table}</span>
                      <span className="text-xs text-gray-500">{tableSchema.rowCount.toLocaleString()} rows</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {tableSchema.columns.map((col) => (
                        <span
                          key={col.name}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                            col.pk ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-gray-800 text-gray-400'
                          }`}
                        >
                          {col.name} <span className="text-gray-600">{col.type}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <div className="flex-1 overflow-auto">
                  {dbLoading ? (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">Loading...</div>
                  ) : (queryResult || tableData).length > 0 ? (
                    <table className="w-full text-xs">
                      <thead className="bg-[#21262d] sticky top-0">
                        <tr>
                          {Object.keys((queryResult || tableData)[0]).map((col) => (
                            <th key={col} className="px-2 py-1.5 text-left font-medium text-gray-300 border-b border-gray-700 whitespace-nowrap">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(queryResult || tableData).map((row, i) => (
                          <tr key={i} className="hover:bg-gray-800/30 border-b border-gray-800/30">
                            {Object.values(row).map((val, j) => (
                              <td key={j} className="px-2 py-1 font-mono text-[11px] max-w-[200px] truncate text-gray-300">
                                {val === null ? <span className="text-gray-600 italic">NULL</span> : String(val)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                      {selectedTable ? 'No data in table' : 'Select a table to view data'}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {!queryResult && selectedTable && tableSchema && tableSchema.rowCount > 0 && (
                  <div className="px-3 py-2 border-t border-gray-800 bg-[#161b22] flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      {dbOffset + 1}-{Math.min(dbOffset + dbLimit, tableSchema.rowCount)} of {tableSchema.rowCount.toLocaleString()}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => selectedTable && loadTableData(selectedTable, Math.max(0, dbOffset - dbLimit))}
                        disabled={dbOffset === 0 || dbLoading}
                        className="px-2 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-gray-800 rounded flex items-center gap-1 transition-colors"
                      >
                        <ChevronLeft className="w-3 h-3" /> Prev
                      </button>
                      <button
                        onClick={() => selectedTable && loadTableData(selectedTable, dbOffset + dbLimit)}
                        disabled={dbOffset + dbLimit >= tableSchema.rowCount || dbLoading}
                        className="px-2 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-gray-800 rounded flex items-center gap-1 transition-colors"
                      >
                        Next <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>

          {/* Bottom resize handle */}
          <div
            className="hidden md:flex h-1.5 shrink-0 cursor-row-resize items-center justify-center bg-transparent hover:bg-cyan-500/20 transition-colors group"
            onMouseDown={(e) => handleResizeStart('bottom', e)}
          >
            <div className="h-0.5 w-16 bg-gray-700 group-hover:bg-cyan-400 rounded-full transition-colors" />
          </div>

          {/* Bottom Drawer - inside center column, hidden on mobile */}
          <div className="hidden md:block shrink-0 overflow-hidden" style={{ height: bottomDrawerHeight }}>
            <BottomDrawer logs={logs} />
          </div>
        </div>

        {/* Right resize handle */}
        <div
          className="hidden lg:flex w-1.5 shrink-0 cursor-col-resize items-center justify-center bg-transparent hover:bg-cyan-500/20 transition-colors group"
          onMouseDown={(e) => handleResizeStart('right', e)}
        >
          <div className="w-0.5 h-16 bg-gray-700 group-hover:bg-cyan-400 rounded-full transition-colors" />
        </div>

        {/* Right Panel - Orchestrator for each terminal - hidden on mobile */}
        <div className="hidden lg:block shrink-0 min-h-0 overflow-hidden orchestrator-panel relative" style={{ width: rightPanelWidth }}>
          {terminalSlots.map(slot => (
            <div key={slot.id} className={`absolute inset-0 ${activeTerminalId === slot.id ? '' : 'hidden'}`}>
              <OrchestratorPanel sessionId={slot.id} visible={activeTerminalId === slot.id} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClaudeLogsPage;
